import { useCallback, useMemo, useRef } from 'react';
import { CANDIDATE_PROFILE_CONFIG, PROFILE_REQUESTS } from 'features/inventory-profiles/constants';
import { buildProfileEndpoint } from 'features/inventory-profiles/utils';
import { useCreateProfileView } from 'hooks/createProfileView';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { RequestStatus, UIStatus } from 'UI/constants/status';
import useFetchCandidateDataSheet from 'UI/pages/EditCandidateDataSheet/hooks/useFetchCandidateDataSheet';

const INITIAL_CANDIDATE = {};

/**
 * Candidate profile, its data sheet, the candidate types and the reference releases. As in V1,
 * the profile waits for the data sheet and the candidate types before it shows.
 * Tracks the view once the candidate has loaded.
 * @param {number|string} id Candidate id
 * @returns {{
 *   candidate: Object,
 *   candidateTypes: Array,
 *   dataSheet: Object | undefined,
 *   dataSheetResponseStatus: string,
 *   error: { request: string, statusCode?: number } | null,
 *   hiringName: string | undefined,
 *   isAlreadyAccountable: boolean,
 *   isLoading: boolean,
 *   refresh: () => void,
 *   refreshReferenceReleases: () => void,
 *   reload: () => void
 * }}
 */
export const useCandidateProfile = id => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const latestRequestRef = useRef(null);
  const latestReleasesRequestRef = useRef(null);

  const candidateEndpoint = buildProfileEndpoint({
    entity: CANDIDATE_PROFILE_CONFIG,
    id,
    isEnabled: checkIfFeatureFlagEnabled
  });

  const loadProfile = useCallback(() => {
    const profileRequest = API.get(candidateEndpoint, { apiVersion: 2 });
    latestRequestRef.current = profileRequest;

    const resolveWithLatestRequest = () => latestRequestRef.current;

    return profileRequest.then(resolveWithLatestRequest, resolveWithLatestRequest);
  }, [candidateEndpoint]);

  const { state, refreshData } = useFetchWithStatusV2({
    promise: id ? loadProfile : undefined,
    initialState: INITIAL_CANDIDATE
  });

  const { status, results, responseStatusCode } = state;
  const isProfileLoading = status === UIStatus.Loading;
  const profile = isProfileLoading ? INITIAL_CANDIDATE : results ?? INITIAL_CANDIDATE;

  useCreateProfileView({ id: profile.id, endpoint: CANDIDATE_PROFILE_CONFIG.trackingUrl });

  const dataSheetResponse = useFetchCandidateDataSheet(id);
  const candidateTypes = useFetchWithStatusV2({
    endpoint: Endpoints.CandidateTypes,
    initialState: []
  });
  const referenceReleasesEndpoint = profile.id
    ? `${Endpoints.Candidates}/${profile.id}/${Endpoints.ReferenceReleases}`
    : null;
  const loadReferenceReleases = useCallback(() => {
    const releasesRequest = API.get(referenceReleasesEndpoint).then(({ data }) => ({
      data: { candidateId: profile.id, releases: data }
    }));
    latestReleasesRequestRef.current = releasesRequest;

    const resolveWithLatestRequest = () => latestReleasesRequestRef.current;

    return releasesRequest.then(resolveWithLatestRequest, resolveWithLatestRequest);
  }, [profile.id, referenceReleasesEndpoint]);

  const referenceReleases = useFetchWithStatusV2({
    promise: referenceReleasesEndpoint ? loadReferenceReleases : undefined,
    initialState: null
  });
  const loadedReleases = referenceReleases.state.results;

  const candidate = useMemo(() => {
    if (!profile.id) return profile;

    const belongsToCandidate = loadedReleases?.candidateId === profile.id;
    return { ...profile, referenceReleases: belongsToCandidate ? loadedReleases.releases : [] };
  }, [profile, loadedReleases]);

  return {
    candidate,
    candidateTypes: candidateTypes.state.results ?? [],
    dataSheet: dataSheetResponse.dataSheet,
    dataSheetResponseStatus: dataSheetResponse.status,
    error:
      status === UIStatus.Error
        ? { request: PROFILE_REQUESTS.Profile, statusCode: responseStatusCode }
        : null,
    hiringName: candidate.relation?.hiringAuthority?.full_name,
    isAlreadyAccountable: candidate.recruiter_id === user.id,
    isLoading:
      isProfileLoading ||
      dataSheetResponse.status === RequestStatus.Loading ||
      candidateTypes.state.status === UIStatus.Loading,
    refresh: () => refreshData(false),
    refreshReferenceReleases: () => referenceReleases.refreshData(false),
    reload: () => refreshData()
  };
};
